import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Calendar, Eye, Tag, ArrowRight, Search } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import Navbar from '../components/Navbar';
import useSEO from '../hooks/useSEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BlogPage() {
  useSEO({
    title: 'Blog | Krieger Real Estate - Real Estate Insights',
    description: 'Expert insights on real estate investing, market trends, and agent success strategies from Krieger Real Estate.',
    canonical: 'https://joinkrieger.com/blog',
  });

  const [posts, setPosts] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPosts = async () => {
      setLoading(true);
      try {
        const params = { page, limit: 9 };
        if (selectedCategory) params.category = selectedCategory;
        const res = await axios.get(`${API}/blog/posts`, { params });
        setPosts(res.data.posts);
        setTotal(res.data.total);
        setPages(res.data.pages);
      } catch { /* blog fetch */ }
      setLoading(false);
    };
    fetchPosts();
  }, [page, selectedCategory]);

  useEffect(() => {
    axios.get(`${API}/blog/categories`).then(res => setCategories(res.data)).catch(() => {});
  }, []);

  const filteredPosts = searchQuery
    ? posts.filter(p => p.title.toLowerCase().includes(searchQuery.toLowerCase()) || (p.excerpt || '').toLowerCase().includes(searchQuery.toLowerCase()))
    : posts;

  const formatDate = (d) => {
    if (!d) return '';
    return new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Hero */}
      <div className="bg-[#0F1729] text-white pt-28 pb-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <p className="text-xs tracking-[0.3em] uppercase text-blue-400 font-semibold mb-3">Insights & Strategy</p>
          <h1 className="text-4xl sm:text-5xl font-bold font-['Cabinet_Grotesk'] mb-4">
            Real Estate Blog
          </h1>
          <p className="text-lg text-slate-400 max-w-2xl">
            Market analysis, deal strategies, and industry insights for real estate professionals.
          </p>
          <div className="mt-8 max-w-md relative">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <Input
              placeholder="Search articles..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="pl-10 bg-white/10 border-white/20 text-white placeholder:text-slate-500 focus:bg-white/15"
              data-testid="blog-search"
            />
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12">
        {/* Categories */}
        {categories.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-10" data-testid="blog-categories">
            <button
              onClick={() => { setSelectedCategory(null); setPage(1); }}
              className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all ${!selectedCategory ? 'bg-[#002FA7] text-white' : 'bg-white text-slate-600 border border-slate-200 hover:border-[#002FA7] hover:text-[#002FA7]'}`}
            >
              All
            </button>
            {categories.map(c => (
              <button
                key={c.category}
                onClick={() => { setSelectedCategory(c.category); setPage(1); }}
                className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all ${selectedCategory === c.category ? 'bg-[#002FA7] text-white' : 'bg-white text-slate-600 border border-slate-200 hover:border-[#002FA7] hover:text-[#002FA7]'}`}
              >
                {c.category} ({c.count})
              </button>
            ))}
          </div>
        )}

        {/* Posts Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map(i => (
              <div key={i} className="bg-white rounded-xl border border-slate-200 overflow-hidden animate-pulse">
                <div className="h-48 bg-slate-200" />
                <div className="p-5 space-y-3">
                  <div className="h-4 bg-slate-200 rounded w-1/3" />
                  <div className="h-6 bg-slate-200 rounded w-3/4" />
                  <div className="h-4 bg-slate-200 rounded" />
                  <div className="h-4 bg-slate-200 rounded w-2/3" />
                </div>
              </div>
            ))}
          </div>
        ) : filteredPosts.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-slate-400 text-lg">No articles found.</p>
            <p className="text-slate-400 text-sm mt-1">Check back soon for new content.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="blog-posts-grid">
            {filteredPosts.map((post, i) => (
              <Link
                key={post.id}
                to={`/blog/${post.slug}`}
                className="group bg-white rounded-xl border border-slate-200 overflow-hidden hover:shadow-lg hover:border-[#002FA7]/30 transition-all duration-300"
                data-testid={`blog-post-card-${i}`}
              >
                {post.featured_image && (
                  <div className="h-48 overflow-hidden">
                    <img src={post.featured_image} alt={post.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  </div>
                )}
                {!post.featured_image && (
                  <div className="h-48 bg-gradient-to-br from-[#002FA7]/10 to-[#0F1729]/10 flex items-center justify-center">
                    <span className="text-4xl font-['Cabinet_Grotesk'] font-bold text-[#002FA7]/20">{post.title.charAt(0)}</span>
                  </div>
                )}
                <div className="p-5">
                  <div className="flex items-center gap-3 mb-3">
                    {post.category && (
                      <Badge variant="secondary" className="text-[10px] uppercase tracking-wider">{post.category}</Badge>
                    )}
                    <span className="flex items-center gap-1 text-xs text-slate-400">
                      <Calendar size={12} />
                      {formatDate(post.published_at)}
                    </span>
                  </div>
                  <h2 className="text-lg font-bold font-['Cabinet_Grotesk'] text-slate-900 group-hover:text-[#002FA7] transition-colors mb-2 line-clamp-2">
                    {post.title}
                  </h2>
                  {post.excerpt && (
                    <p className="text-sm text-slate-500 line-clamp-3 mb-4">{post.excerpt}</p>
                  )}
                  <div className="flex items-center justify-between">
                    <span className="flex items-center gap-1 text-xs text-slate-400">
                      <Eye size={12} /> {post.views || 0} views
                    </span>
                    <span className="text-sm font-semibold text-[#002FA7] flex items-center gap-1 group-hover:gap-2 transition-all">
                      Read <ArrowRight size={14} />
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}

        {/* Pagination */}
        {pages > 1 && (
          <div className="flex justify-center gap-2 mt-12" data-testid="blog-pagination">
            {Array.from({ length: pages }, (_, i) => i + 1).map(p => (
              <button
                key={p}
                onClick={() => setPage(p)}
                className={`w-10 h-10 rounded-full text-sm font-medium transition-all ${p === page ? 'bg-[#002FA7] text-white' : 'bg-white text-slate-600 border border-slate-200 hover:border-[#002FA7]'}`}
              >
                {p}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* CTA */}
      <div className="bg-[#0F1729] py-16">
        <div className="max-w-3xl mx-auto text-center px-4">
          <h2 className="text-2xl sm:text-3xl font-bold font-['Cabinet_Grotesk'] text-white mb-4">
            Ready to Earn 90% Commission?
          </h2>
          <p className="text-slate-400 mb-8">Join the fastest-growing real estate brokerage.</p>
          <Link to="/apply">
            <Button className="bg-[#002FA7] hover:bg-[#0025a0] text-white px-8 py-3 rounded-full text-sm font-semibold" data-testid="blog-cta-apply">
              Apply Now
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}
